import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  CloudUpload,
  ContentCopy,
  Delete,
  Download,
  Error as ErrorIcon,
  Link as LinkIcon,
  QrCode2,
  Refresh,
  Send,
  Sync,
  Visibility,
  VisibilityOff,
  Webhook,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { efiApi, api } from '@/services/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentCompany } from '@/store/slices/authSlice';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface EfiConfig {
  configured: boolean;
  environment: 'producao' | 'homologacao';
  clientId?: string;
  hasClientSecret: boolean;
  pixKey?: string;
  hasCertificate: boolean;
  certificate?: {
    subject: string;
    issuer: string;
    thumbprint: string;
    validFrom: string;
    expiresAt: string;
    daysUntilExpiry: number | null;
  } | null;
  webhook?: {
    id: string;
    url: string;
    skipMtls: boolean;
    checkIp: boolean;
    allowedIps: string[];
    registeredUrl?: string | null;
    registeredAt?: string | null;
    lastError?: string | null;
  };
  lastTokenAt?: string | null;
  lastError?: string | null;
}

interface Report {
  id: string;
  efi_report_id: string;
  data_movimento: string;
  status: string;
  total_registros: number | null;
  totais: { creditos?: number; debitos?: number; porTipo?: Record<string, number> } | null;
  saldo_dia_anterior: string | null;
  saldo_dia: string | null;
  error_message?: string | null;
  requested_at: string;
  downloaded_at?: string | null;
}

interface Registro {
  id: string;
  data_movimento: string;
  tipo: string;
  descricao_tipo: string;
  natureza: 'C' | 'D' | 'S' | 'N';
  e2e_id?: string;
  txid?: string;
  valor: string | null;
  documento?: string;
  nome?: string;
  info_pagador?: string;
  horario?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const brl = (v: number | string | null | undefined) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (v?: string | null, withTime = true) => {
  if (!v) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v.split('-').reverse().join('/'); // data pura (sem fuso)
  try {
    return format(new Date(v), withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return v;
  }
};

const isoDay = (d: Date) => d.toISOString().substring(0, 10);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

const errMsg = (e: any, fallback: string) =>
  e?.response?.data?.error?.message || e?.error?.message || e?.message || fallback;

const copy = (text: string, label = 'Copiado') => {
  navigator.clipboard.writeText(text).then(() => toast.success(label));
};

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const StatusChip: React.FC<{ status?: string }> = ({ status }) => {
  const s = String(status || '').toUpperCase();
  const color: 'success' | 'warning' | 'error' | 'default' | 'info' =
    ['CONCLUIDA', 'CONCLUIDO', 'REALIZADO', 'PROCESSED', 'ATIVA'].includes(s) ? 'success'
    : ['EM_PROCESSAMENTO', 'AGUARDANDO_PROCESSAMENTO', 'SOLICITADO', 'PROCESSING'].includes(s) ? 'warning'
    : ['NAO_REALIZADO', 'ERRO', 'FAILED', 'DEVOLVIDA'].includes(s) ? 'error'
    : 'default';
  return <Chip size="small" label={s || '—'} color={color} variant="outlined" />;
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

const EfiSettingsPage: React.FC = () => {
  const currentCompany = useAppSelector(selectCurrentCompany);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<EfiConfig | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      const res = await efiApi.getConfig();
      if (res.success && res.data) setConfig(res.data);
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao carregar configuração Efí'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadConfig();
  }, [loadConfig, currentCompany?.id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Integração Efí</Typography>
          <Typography variant="body2" color="text.secondary">
            API Pix da Efí (ex-Gerencianet): credenciais, certificado mTLS, webhook, conta e extrato de conciliação.
            {currentCompany?.name ? ` Empresa: ${currentCompany.name}.` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={config?.environment === 'producao' ? 'Produção' : 'Homologação'}
            color={config?.environment === 'producao' ? 'primary' : 'default'}
          />
          <Chip
            size="small"
            icon={config?.configured ? <CheckCircle /> : <ErrorIcon />}
            label={config?.configured ? 'Configurada' : 'Incompleta'}
            color={config?.configured ? 'success' : 'warning'}
            variant="outlined"
          />
        </Stack>
      </Box>

      {config?.lastError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Último erro</AlertTitle>
          {config.lastError}
        </Alert>
      )}

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Credenciais & Certificado" />
          <Tab icon={<Webhook fontSize="small" />} iconPosition="start" label="Webhook" />
          <Tab icon={<QrCode2 fontSize="small" />} iconPosition="start" label="Conta & Pix" disabled={!config?.configured} />
          <Tab icon={<Sync fontSize="small" />} iconPosition="start" label="Conciliação" disabled={!config?.configured} />
        </Tabs>
        <Divider />
        <Box p={3}>
          {tab === 0 && <CredentialsTab config={config} onChange={loadConfig} />}
          {tab === 1 && <WebhookTab config={config} onChange={loadConfig} />}
          {tab === 2 && <AccountTab config={config} />}
          {tab === 3 && <ReconciliationTab />}
        </Box>
      </Paper>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Aba 1 — Credenciais & certificado
// ---------------------------------------------------------------------------

const CredentialsTab: React.FC<{ config: EfiConfig | null; onChange: () => Promise<void> }> = ({ config, onChange }) => {
  const [form, setForm] = useState({
    environment: config?.environment || 'homologacao',
    clientId: config?.clientId || '',
    clientSecret: '',
    pixKey: config?.pixKey || '',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPassword, setCertPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  useEffect(() => {
    setForm(f => ({
      ...f,
      environment: config?.environment || 'homologacao',
      clientId: config?.clientId || '',
      pixKey: config?.pixKey || '',
    }));
  }, [config]);

  const save = async () => {
    if (!form.clientId) return toast.error('Informe o Client ID');
    setSaving(true);
    try {
      const res = await efiApi.saveConfig({
        environment: form.environment,
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim() || undefined,
        pixKey: form.pixKey.trim(),
      });
      if (res.success) {
        toast.success('Credenciais salvas');
        setForm(f => ({ ...f, clientSecret: '' }));
        await onChange();
      }
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const upload = async () => {
    if (!certFile) return toast.error('Selecione o arquivo .p12');
    setUploading(true);
    try {
      const b64 = await fileToBase64(certFile);
      const res = await efiApi.uploadCertificate({ certificate: b64, password: certPassword });
      if (res.success) {
        toast.success('Certificado salvo');
        setCertFile(null);
        setCertPassword('');
        await onChange();
      }
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao enviar certificado'));
    } finally {
      setUploading(false);
    }
  };

  const removeCert = async () => {
    if (!window.confirm('Remover o certificado Efí desta empresa?')) return;
    try {
      await efiApi.deleteCertificate();
      toast.success('Certificado removido');
      await onChange();
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao remover'));
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await efiApi.testConnection();
      setTestResult(res.data);
      if (res.data?.success) toast.success('Conexão OK');
      else toast.error(res.data?.message || 'Falha na conexão');
      await onChange();
    } catch (e: any) {
      setTestResult({ success: false, message: errMsg(e, 'Erro ao testar') });
    } finally {
      setTesting(false);
    }
  };

  const cert = config?.certificate;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Aplicação (API → Aplicações)</Typography>
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Ambiente</InputLabel>
            <Select
              label="Ambiente"
              value={form.environment}
              onChange={e => setForm({ ...form, environment: e.target.value as any })}
            >
              <MenuItem value="homologacao">Homologação (pix-h.api.efipay.com.br)</MenuItem>
              <MenuItem value="producao">Produção (pix.api.efipay.com.br)</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Client ID" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} fullWidth />
          <TextField
            size="small"
            label="Client Secret"
            type={showSecret ? 'text' : 'password'}
            value={form.clientSecret}
            onChange={e => setForm({ ...form, clientSecret: e.target.value })}
            placeholder={config?.hasClientSecret ? '•••••••• (já salvo — preencha só para trocar)' : ''}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowSecret(s => !s)}>
                    {showSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            label="Chave Pix da conta Efí"
            helperText="Chave usada para receber cobranças, enviar Pix e associar o webhook (CPF/CNPJ, e-mail, telefone ou aleatória)"
            value={form.pixKey}
            onChange={e => setForm({ ...form, pixKey: e.target.value })}
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Salvando…' : 'Salvar credenciais'}</Button>
            <Button variant="outlined" onClick={test} disabled={testing || !config?.configured} startIcon={testing ? <CircularProgress size={16} /> : <LinkIcon />}>
              Testar conexão
            </Button>
          </Stack>
          {testResult && (
            <Alert severity={testResult.success ? 'success' : 'error'}>
              {testResult.message}
              {testResult.scopes?.length ? (
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  {testResult.scopes.map((s: string) => <Chip key={s} size="small" label={s} />)}
                </Box>
              ) : null}
            </Alert>
          )}
        </Stack>
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Certificado (API → Meus Certificados)</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Não é o A1 ICP-Brasil. O certificado é gerado no painel da Efí, em formato <b>.p12</b>, sem senha, e
          o download só é permitido uma vez. Ele é obrigatório em todas as chamadas (mTLS), inclusive no OAuth.
        </Alert>

        {cert ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="body2"><b>Titular:</b> {cert.subject || '—'}</Typography>
                <Typography variant="body2"><b>Emissor:</b> {cert.issuer || '—'}</Typography>
                <Typography variant="body2"><b>Validade:</b> {fmtDate(cert.validFrom, false)} → {fmtDate(cert.expiresAt, false)}</Typography>
                <Typography variant="caption" color="text.secondary">Thumbprint: {cert.thumbprint}</Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={1}>
                <Chip
                  size="small"
                  color={(cert.daysUntilExpiry ?? 0) > 30 ? 'success' : (cert.daysUntilExpiry ?? 0) > 0 ? 'warning' : 'error'}
                  label={cert.daysUntilExpiry !== null ? `${cert.daysUntilExpiry} dias` : '—'}
                />
                <Tooltip title="Remover certificado">
                  <IconButton size="small" color="error" onClick={removeCert}><Delete fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>Nenhum certificado enviado.</Alert>
        )}

        <Stack spacing={2}>
          <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
            {certFile ? certFile.name : 'Selecionar arquivo .p12 / .pfx'}
            <input type="file" hidden accept=".p12,.pfx" onChange={e => setCertFile(e.target.files?.[0] || null)} />
          </Button>
          <TextField
            size="small"
            label="Senha do certificado (normalmente vazia)"
            type="password"
            value={certPassword}
            onChange={e => setCertPassword(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={upload} disabled={!certFile || uploading}>
            {uploading ? 'Enviando…' : cert ? 'Substituir certificado' : 'Enviar certificado'}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// Aba 2 — Webhook
// ---------------------------------------------------------------------------

const WebhookTab: React.FC<{ config: EfiConfig | null; onChange: () => Promise<void> }> = ({ config, onChange }) => {
  const wh = config?.webhook;
  const [remote, setRemote] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [skipMtls, setSkipMtls] = useState(wh?.skipMtls ?? true);
  const [checkIp, setCheckIp] = useState(wh?.checkIp ?? true);

  useEffect(() => {
    setSkipMtls(wh?.skipMtls ?? true);
    setCheckIp(wh?.checkIp ?? true);
  }, [wh?.skipMtls, wh?.checkIp]);

  const loadRemote = useCallback(async () => {
    if (!config?.configured) return;
    try {
      const [r, l] = await Promise.all([efiApi.getWebhook(), efiApi.webhookLogs(50)]);
      setRemote(r.data);
      setLogs(l.data || []);
    } catch (e: any) {
      setRemote({ registered: false, error: errMsg(e, 'Erro ao consultar') });
    }
  }, [config?.configured]);

  useEffect(() => { loadRemote(); }, [loadRemote]);

  const run = async (key: string, fn: () => Promise<any>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      await onChange();
      await loadRemote();
    } catch (e: any) {
      toast.error(errMsg(e, 'Falhou'));
    } finally {
      setBusy(null);
    }
  };

  const saveFlags = (next: { skipMtls?: boolean; checkIp?: boolean }) =>
    run('flags', () => efiApi.saveConfig({
      environment: config!.environment,
      clientId: config!.clientId || '',
      pixKey: config!.pixKey,
      webhookSkipMtls: next.skipMtls ?? skipMtls,
      webhookCheckIp: next.checkIp ?? checkIp,
    }), 'Preferências salvas');

  if (!config?.configured) {
    return <Alert severity="warning">Salve as credenciais e envie o certificado antes de configurar o webhook.</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Endereço do webhook</Typography>
        <TextField
          size="small"
          fullWidth
          value={wh?.url || ''}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => copy(wh?.url || '', 'URL copiada')}><ContentCopy fontSize="small" /></IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary">
          O parâmetro <code>hmac</code> identifica sua empresa e é validado em cada chamada; <code>ignorar=</code> evita que a Efí
          acrescente <code>/pix</code> ao final.
        </Typography>

        <Box mt={2}>
          <FormControlLabel
            control={<Switch checked={skipMtls} onChange={(_, v) => { setSkipMtls(v); saveFlags({ skipMtls: v }); }} disabled={busy === 'flags'} />}
            label={<Typography variant="body2">Registrar com <code>x-skip-mtls-checking</code> (a Efí não exige mTLS no nosso servidor)</Typography>}
          />
          <FormControlLabel
            control={<Switch checked={checkIp} onChange={(_, v) => { setCheckIp(v); saveFlags({ checkIp: v }); }} disabled={busy === 'flags'} />}
            label={<Typography variant="body2">Aceitar somente o IP de origem da Efí ({wh?.allowedIps?.join(', ')})</Typography>}
          />
          <Alert severity={skipMtls ? 'info' : 'warning'} sx={{ mt: 1 }}>
            {skipMtls
              ? 'O TLS público é terminado pelo Traefik, que não faz mTLS. Neste modo a segurança é dada pelo HMAC na URL + filtro de IP (recomendação da própria Efí quando se dispensa o mTLS).'
              : 'Com mTLS ativo a Efí vai exigir que o servidor apresente/valide certificado cliente. Só funciona se o listener EFI_WEBHOOK_MTLS_PORT estiver configurado e exposto — veja backend/src/services/efi/efi-mtls-server.js.'}
          </Alert>
        </Box>

        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
          <Button variant="contained" startIcon={<Webhook />} disabled={!!busy || !config.pixKey}
            onClick={() => run('reg', () => efiApi.registerWebhook(), 'Webhook registrado na Efí')}>
            Registrar na Efí
          </Button>
          <Button variant="outlined" color="error" disabled={!!busy}
            onClick={() => window.confirm('Remover o webhook na Efí?') && run('del', () => efiApi.unregisterWebhook(), 'Webhook removido')}>
            Remover
          </Button>
          <Button variant="text" disabled={!!busy}
            onClick={() => window.confirm('Gerar novo endereço/HMAC? Será preciso registrar de novo.') && run('regen', () => efiApi.regenerateWebhook(), 'Novo endereço gerado')}>
            Regenerar HMAC
          </Button>
          <Button variant="text" startIcon={<Refresh />} onClick={loadRemote}>Atualizar</Button>
        </Stack>
        {!config.pixKey && <Alert severity="warning" sx={{ mt: 1 }}>Informe a chave Pix da conta na aba de credenciais para registrar o webhook.</Alert>}
        {wh?.lastError && <Alert severity="error" sx={{ mt: 1 }}>{wh.lastError}</Alert>}
      </Grid>

      <Grid item xs={12} md={5}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Situação na Efí</Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          {remote === null ? <CircularProgress size={20} /> : remote.registered ? (
            <Stack spacing={0.5}>
              <Chip size="small" color="success" label="Registrado" sx={{ alignSelf: 'flex-start' }} />
              <Typography variant="body2"><b>Chave:</b> {remote.chave}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}><b>URL na Efí:</b> {remote.webhookUrl}</Typography>
              <Typography variant="body2"><b>Criado em:</b> {fmtDate(remote.criacao)}</Typography>
              {wh?.url && remote.webhookUrl && remote.webhookUrl !== wh.url && (
                <Alert severity="warning">A URL registrada na Efí é diferente da atual — registre novamente.</Alert>
              )}
            </Stack>
          ) : (
            <Stack spacing={0.5}>
              <Chip size="small" color="default" label="Não registrado" sx={{ alignSelf: 'flex-start' }} />
              {remote.error && <Typography variant="caption" color="error">{remote.error}</Typography>}
            </Stack>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Últimas notificações recebidas</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recebido</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Evento</TableCell>
                <TableCell>E2E / txid</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nenhum webhook recebido ainda</TableCell></TableRow>}
              {logs.map(l => (
                <TableRow key={l.id} hover>
                  <TableCell>{fmtDate(l.created_at)}</TableCell>
                  <TableCell>{l.webhook_type}</TableCell>
                  <TableCell>{l.event_type}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{l.end_to_end_id || l.txid || '—'}</TableCell>
                  <TableCell>{l.ip_address}</TableCell>
                  <TableCell>
                    <Tooltip title={l.processing_error || ''}><span><StatusChip status={l.status} /></span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// Aba 3 — Conta & Pix
// ---------------------------------------------------------------------------

const AccountTab: React.FC<{ config: EfiConfig | null }> = ({ config }) => {
  const [saldo, setSaldo] = useState<any>(null);
  const [saldoErr, setSaldoErr] = useState<string | null>(null);
  const [inicio, setInicio] = useState(daysAgo(7));
  const [fim, setFim] = useState(isoDay(new Date()));
  const [recebidos, setRecebidos] = useState<any[]>([]);
  const [enviados, setEnviados] = useState<any[]>([]);
  const [loadingPix, setLoadingPix] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Nova cobrança
  const [cobOpen, setCobOpen] = useState(false);
  const [cob, setCob] = useState({ valor: '', solicitacaoPagador: '', nome: '', documento: '', expiracao: '3600' });
  const [cobResult, setCobResult] = useState<any>(null);
  const [cobBusy, setCobBusy] = useState(false);

  // Enviar Pix
  const [sendOpen, setSendOpen] = useState(false);
  const [send, setSend] = useState({ valor: '', chaveFavorecido: '', infoPagador: '' });
  const [sendBusy, setSendBusy] = useState(false);

  const loadSaldo = useCallback(async () => {
    setSaldoErr(null);
    try {
      const r = await efiApi.saldo();
      setSaldo(r.data);
    } catch (e: any) {
      setSaldoErr(errMsg(e, 'Erro ao consultar saldo (escopo gn.balance.read?)'));
    }
  }, []);

  const loadPix = useCallback(async () => {
    setLoadingPix(true);
    try {
      const [r, s] = await Promise.allSettled([
        efiApi.pixRecebidos({ inicio, fim }),
        efiApi.pixEnviados({ inicio, fim }),
      ]);
      if (r.status === 'fulfilled') setRecebidos(r.value.data?.pix || []);
      else toast.error(errMsg(r.reason, 'Erro ao listar recebidos'));
      if (s.status === 'fulfilled') setEnviados(s.value.data?.pix || []);
      else setEnviados([]);
    } finally {
      setLoadingPix(false);
    }
  }, [inicio, fim]);

  useEffect(() => { loadSaldo(); }, [loadSaldo]);
  useEffect(() => { loadPix(); }, [loadPix]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await efiApi.syncPix({ inicio, fim });
      toast.success(`Sincronizado: ${r.data?.received} recebidos (${r.data?.newReceived} novos), ${r.data?.sent} enviados (${r.data?.newSent} novos)`);
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao sincronizar'));
    } finally {
      setSyncing(false);
    }
  };

  const createCob = async () => {
    if (!cob.valor) return toast.error('Informe o valor');
    setCobBusy(true);
    try {
      const devedor = cob.nome && cob.documento
        ? (cob.documento.replace(/\D/g, '').length > 11
          ? { cnpj: cob.documento, nome: cob.nome }
          : { cpf: cob.documento, nome: cob.nome })
        : undefined;
      const r = await efiApi.createCob({
        valor: cob.valor.replace(',', '.'),
        solicitacaoPagador: cob.solicitacaoPagador || undefined,
        expiracao: Number(cob.expiracao) || 3600,
        devedor,
      });
      setCobResult(r.data);
      toast.success('Cobrança criada');
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao criar cobrança'));
    } finally {
      setCobBusy(false);
    }
  };

  const sendPix = async () => {
    if (!send.valor || !send.chaveFavorecido) return toast.error('Informe valor e chave do favorecido');
    if (!window.confirm(`Confirmar envio de ${brl(send.valor.replace(',', '.'))} para ${send.chaveFavorecido}?`)) return;
    setSendBusy(true);
    try {
      const r = await efiApi.enviarPix({
        valor: send.valor.replace(',', '.'),
        chaveFavorecido: send.chaveFavorecido.trim(),
        infoPagador: send.infoPagador || undefined,
      });
      toast.success(`Pix ${r.data?.status || 'enviado'} — e2e ${r.data?.e2eId || ''}`);
      setSendOpen(false);
      setSend({ valor: '', chaveFavorecido: '', infoPagador: '' });
      loadSaldo();
      loadPix();
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao enviar Pix'));
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="overline" color="text.secondary">Saldo disponível</Typography>
              <IconButton size="small" onClick={loadSaldo}><Refresh fontSize="small" /></IconButton>
            </Stack>
            {saldoErr ? <Typography variant="body2" color="error">{saldoErr}</Typography> : (
              <>
                <Typography variant="h5" fontWeight={600}>{saldo ? brl(saldo.saldo) : '…'}</Typography>
                {saldo?.bloqueios && Number(saldo.bloqueios.total) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Bloqueios: {brl(saldo.bloqueios.total)} (judicial {brl(saldo.bloqueios.judicial)}, MED {brl(saldo.bloqueios.med)})
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">Chave Pix da conta</Typography>
            <Typography variant="body1" fontWeight={500}>{config?.pixKey || 'não configurada'}</Typography>
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" variant="contained" startIcon={<QrCode2 />} onClick={() => { setCobResult(null); setCobOpen(true); }} disabled={!config?.pixKey}>
                Nova cobrança (QR Code)
              </Button>
              <Button size="small" variant="outlined" startIcon={<Send />} onClick={() => setSendOpen(true)} disabled={!config?.pixKey}>
                Enviar Pix
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField size="small" type="date" label="Início" InputLabelProps={{ shrink: true }} value={inicio} onChange={e => setInicio(e.target.value)} />
        <TextField size="small" type="date" label="Fim" InputLabelProps={{ shrink: true }} value={fim} onChange={e => setFim(e.target.value)} />
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadPix} disabled={loadingPix}>Consultar</Button>
        <Button variant="text" size="small" startIcon={syncing ? <CircularProgress size={14} /> : <Sync />} onClick={sync} disabled={syncing}>
          Sincronizar período para o histórico local
        </Button>
      </Stack>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Pix recebidos ({recebidos.length})</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Horário</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Pagador</TableCell>
                <TableCell>Info</TableCell>
                <TableCell>E2E</TableCell>
                <TableCell>txid</TableCell>
                <TableCell>Devoluções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingPix && <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={18} /></TableCell></TableRow>}
              {!loadingPix && recebidos.length === 0 && <TableRow><TableCell colSpan={7} align="center">Nenhum Pix no período</TableCell></TableRow>}
              {recebidos.map((p: any) => (
                <TableRow key={p.endToEndId} hover>
                  <TableCell>{fmtDate(p.horario)}</TableCell>
                  <TableCell align="right">{brl(p.valor)}</TableCell>
                  <TableCell>{p.gnExtras?.pagador?.nome || p.pagador?.nome || '—'}<br /><Typography variant="caption" color="text.secondary">{p.gnExtras?.pagador?.cpf || p.gnExtras?.pagador?.cnpj || ''}</Typography></TableCell>
                  <TableCell>{p.infoPagador || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.endToEndId}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.txid || '—'}</TableCell>
                  <TableCell>{Array.isArray(p.devolucoes) && p.devolucoes.length ? p.devolucoes.map((d: any) => `${d.status} ${brl(d.valor)}`).join(', ') : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Pix enviados ({enviados.length})</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Solicitado</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Favorecido</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>E2E</TableCell>
                <TableCell>idEnvio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loadingPix && enviados.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nenhum envio no período</TableCell></TableRow>}
              {enviados.map((p: any) => (
                <TableRow key={p.endToEndId || p.idEnvio} hover>
                  <TableCell>{fmtDate(p.horario?.solicitacao || p.horario)}</TableCell>
                  <TableCell align="right">{brl(p.valor)}</TableCell>
                  <TableCell>{p.favorecido?.identificacao?.nome || p.favorecido?.contaBanco?.nome || p.favorecido?.chave || '—'}</TableCell>
                  <TableCell><StatusChip status={p.status} /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.endToEndId || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.idEnvio || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Dialog: nova cobrança */}
      <Dialog open={cobOpen} onClose={() => setCobOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova cobrança Pix (Efí)</DialogTitle>
        <DialogContent>
          {!cobResult ? (
            <Stack spacing={2} mt={1}>
              <TextField size="small" label="Valor (R$)" value={cob.valor} onChange={e => setCob({ ...cob, valor: e.target.value })} autoFocus />
              <TextField size="small" label="Descrição para o pagador" value={cob.solicitacaoPagador} onChange={e => setCob({ ...cob, solicitacaoPagador: e.target.value })} inputProps={{ maxLength: 140 }} />
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Nome do devedor (opcional)" value={cob.nome} onChange={e => setCob({ ...cob, nome: e.target.value })} fullWidth />
                <TextField size="small" label="CPF/CNPJ" value={cob.documento} onChange={e => setCob({ ...cob, documento: e.target.value })} />
              </Stack>
              <TextField size="small" label="Expiração (segundos)" value={cob.expiracao} onChange={e => setCob({ ...cob, expiracao: e.target.value })} />
            </Stack>
          ) : (
            <Stack spacing={1} alignItems="center" mt={1}>
              {cobResult.imagemQrcode && <img src={cobResult.imagemQrcode} alt="QR Code" style={{ width: 220, height: 220 }} />}
              <Typography variant="body2"><b>{brl(cobResult.valor?.original)}</b> · txid <code>{cobResult.txid}</code> · <StatusChip status={cobResult.status} /></Typography>
              <TextField
                size="small" fullWidth multiline minRows={3} value={cobResult.qrcode || cobResult.pixCopiaECola || ''}
                InputProps={{ readOnly: true, endAdornment: <IconButton onClick={() => copy(cobResult.qrcode || cobResult.pixCopiaECola, 'Copia e cola copiado')}><ContentCopy /></IconButton> }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCobOpen(false)}>Fechar</Button>
          {!cobResult && <Button variant="contained" onClick={createCob} disabled={cobBusy}>{cobBusy ? 'Criando…' : 'Gerar QR Code'}</Button>}
        </DialogActions>
      </Dialog>

      {/* Dialog: enviar pix */}
      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Pix pela Efí</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Movimenta dinheiro real em produção. A Efí exige que a chave pagadora ({config?.pixKey}) tenha webhook registrado.
          </Alert>
          <Stack spacing={2}>
            <TextField size="small" label="Valor (R$)" value={send.valor} onChange={e => setSend({ ...send, valor: e.target.value })} autoFocus />
            <TextField size="small" label="Chave Pix do favorecido" value={send.chaveFavorecido} onChange={e => setSend({ ...send, chaveFavorecido: e.target.value })} helperText="Telefone com +55, e-mail, CPF/CNPJ ou chave aleatória" />
            <TextField size="small" label="Mensagem (opcional)" value={send.infoPagador} onChange={e => setSend({ ...send, infoPagador: e.target.value })} inputProps={{ maxLength: 140 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={sendPix} disabled={sendBusy} startIcon={<Send />}>{sendBusy ? 'Enviando…' : 'Enviar'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Aba 4 — Extrato de conciliação
// ---------------------------------------------------------------------------

const ReconciliationTab: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [dia, setDia] = useState(daysAgo(1));
  const [requesting, setRequesting] = useState(false);
  const [fetching, setFetching] = useState<string | null>(null);
  const [selected, setSelected] = useState<Report | null>(null);
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [filtroTipo, setFiltroTipo] = useState('');

  const loadReports = useCallback(async () => {
    try {
      const r = await efiApi.relatorios();
      setReports(r.data || []);
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao listar extratos'));
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  useEffect(() => {
    if (!selected) { setRegistros([]); return; }
    const day = selected.data_movimento.substring(0, 10);
    efiApi.registros({ dataInicio: day, dataFim: day, limit: 5000 })
      .then(r => setRegistros(r.data || []))
      .catch(() => setRegistros([]));
  }, [selected]);

  const request = async () => {
    setRequesting(true);
    try {
      const r = await efiApi.solicitarRelatorio(dia);
      if (r.data?.ready) toast.success('Extrato gerado e processado');
      else toast('Extrato solicitado — a Efí leva ~30 s para gerar. Clique em "Buscar" na lista.', { icon: '⏳' });
      await loadReports();
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao solicitar extrato'));
    } finally {
      setRequesting(false);
    }
  };

  const fetchOne = async (rep: Report) => {
    setFetching(rep.id);
    try {
      const r = await efiApi.buscarRelatorio(rep.id);
      if (r.data?.ready) toast.success('Extrato processado');
      else toast(`Ainda ${r.data?.status || 'em processamento'}`, { icon: '⏳' });
      await loadReports();
    } catch (e: any) {
      toast.error(errMsg(e, 'Erro ao buscar extrato'));
    } finally {
      setFetching(null);
    }
  };

  const downloadCsv = (rep: Report) =>
    api.download(efiApi.csvUrl(rep.id), `extrato-efi-${rep.data_movimento.substring(0, 10)}.csv`)
      .catch((e: any) => toast.error(errMsg(e, 'CSV indisponível')));

  const tipos = useMemo(() => Array.from(new Set(registros.map(r => r.tipo))).sort(), [registros]);
  const visiveis = filtroTipo ? registros.filter(r => r.tipo === filtroTipo) : registros;

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        O extrato de conciliação é o CSV oficial diário da Efí (Pix recebidos, enviados, devoluções, tarifas, transferências e saldos).
        Uma rotina gera automaticamente o do dia anterior às 06:05; aqui você pode gerar qualquer dia passado.
      </Alert>

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" type="date" label="Dia do movimento" InputLabelProps={{ shrink: true }} value={dia} onChange={e => setDia(e.target.value)} inputProps={{ max: daysAgo(1) }} />
        <Button variant="contained" onClick={request} disabled={requesting} startIcon={requesting ? <CircularProgress size={16} /> : <Sync />}>
          {requesting ? 'Aguardando a Efí…' : 'Gerar extrato'}
        </Button>
        <Button variant="text" startIcon={<Refresh />} onClick={loadReports}>Atualizar</Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dia</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Registros</TableCell>
              <TableCell align="right">Créditos</TableCell>
              <TableCell align="right">Débitos</TableCell>
              <TableCell align="right">Saldo anterior</TableCell>
              <TableCell align="right">Saldo do dia</TableCell>
              <TableCell>Solicitado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length === 0 && <TableRow><TableCell colSpan={9} align="center">Nenhum extrato solicitado</TableCell></TableRow>}
            {reports.map(rep => (
              <TableRow key={rep.id} hover selected={selected?.id === rep.id} sx={{ cursor: 'pointer' }} onClick={() => setSelected(rep)}>
                <TableCell>{fmtDate(rep.data_movimento, false)}</TableCell>
                <TableCell>
                  <Tooltip title={rep.error_message || ''}><span><StatusChip status={rep.status} /></span></Tooltip>
                </TableCell>
                <TableCell align="right">{rep.total_registros ?? '—'}</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>{rep.totais ? brl(rep.totais.creditos) : '—'}</TableCell>
                <TableCell align="right" sx={{ color: 'error.main' }}>{rep.totais ? brl(rep.totais.debitos) : '—'}</TableCell>
                <TableCell align="right">{brl(rep.saldo_dia_anterior)}</TableCell>
                <TableCell align="right">{brl(rep.saldo_dia)}</TableCell>
                <TableCell>{fmtDate(rep.requested_at)}</TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  {rep.status !== 'CONCLUIDO' ? (
                    <Button size="small" onClick={() => fetchOne(rep)} disabled={fetching === rep.id}>
                      {fetching === rep.id ? '…' : 'Buscar'}
                    </Button>
                  ) : (
                    <Tooltip title="Baixar CSV"><IconButton size="small" onClick={() => downloadCsv(rep)}><Download fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selected && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Registros de {fmtDate(selected.data_movimento, false)} ({visiveis.length})
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {tipos.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Horário</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Nome / Documento</TableCell>
                  <TableCell>Info</TableCell>
                  <TableCell>E2E / txid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiveis.length === 0 && <TableRow><TableCell colSpan={6} align="center">Sem registros</TableCell></TableRow>}
                {visiveis.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{fmtDate(r.horario)}</TableCell>
                    <TableCell><Tooltip title={r.descricao_tipo}><Chip size="small" label={r.tipo} variant="outlined" /></Tooltip></TableCell>
                    <TableCell align="right" sx={{ color: r.natureza === 'C' ? 'success.main' : r.natureza === 'D' ? 'error.main' : 'text.primary' }}>
                      {r.natureza === 'D' ? '- ' : ''}{brl(r.valor)}
                    </TableCell>
                    <TableCell>{r.nome || '—'}{r.documento ? <Typography variant="caption" color="text.secondary" display="block">{r.documento}</Typography> : null}</TableCell>
                    <TableCell>{r.info_pagador || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.e2e_id || r.txid || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
};

export default EfiSettingsPage;
